import {
  ArrowLeft,
  Building,
  Code,
  Monitor,
  Terminal,
  Users,
} from "lucide-react";
import { useEffect } from "react";
import {
  Form,
  Link,
  useActionData,
  useNavigate,
  useNavigation,
} from "react-router-dom";
import { ForgotPasswordEnterEmailActionResponse } from "./types/forgotPasswordEnterEmailTypes";
import { useTranslation } from "react-i18next";

const ForgotPassword = () => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const navigate = useNavigate();
  const actionData = useActionData() as ForgotPasswordEnterEmailActionResponse;
  const isSubmitting = navigation.state === "submitting";

  useEffect(() => {
    if (!actionData) return;
    if (actionData.shouldRedirect && navigation.state === "idle") {
      navigate("/verify-code", {
        state: { email: actionData.email },
      });
    }
  }, [actionData, navigation.state, navigate]);

  return (
    <div className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl overflow-hidden">
      {/* Grid for separating form and  Coding Illustration Section */}
      <div className="grid lg:grid-cols-2 min-h-[600px]">
        {/* Form Card Section */}
        <div className="flex flex-col justify-center p-8 lg:p-12">
          <div className="w-full max-w-md mx-auto">
            {/* Header Section */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-primary rounded-lg p-2">
                  <Code className="h-6 w-6 text-white" />
                </div>{" "}
              </div>
              <h1 className="text-2xl font-bold">{t('forgotPassword.title')}</h1>
              <p className="text-gray-600 mt-2">
                {t('forgotPassword.subtitle')}
              </p>
            </div>
            {/* Form Section */}
            <Form
              method="post"
              className={`space-y-4 ${isSubmitting ? "animate-pulse" : ""}`}
            >
              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t('auth.email')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="ornek@email.com"
                  className="w-full h-11 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>

              <button
                disabled={isSubmitting}
                type="submit"
                className={`w-full h-11 text-base font-medium text-white rounded-md transition-colors duration-300 ${
                  isSubmitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-primary hover:bg-primary-600 hover:shadow-lg cursor-pointer"
                }`}
              >
                {isSubmitting ? t('forgotPassword.sending') : t('forgotPassword.sendResetLink')}
              </button>
              <Link
                to="/login"
                className="flex items-center justify-center text-center text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                <ArrowLeft className="inline h-5 w-5 mr-1 align-text-bottom" />
                <p>{t('forgotPassword.backToLogin')}</p>
              </Link>
            </Form>
          </div>
        </div>
        {/* Coding Illustration Section */}
        <div className="bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-8 lg:p-12">
          <div className="relative w-full max-w-lg">
            {/* Main Illustration Container */}
            <div className="relative bg-white rounded-2xl shadow-xl p-6 border">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2">
                  <Terminal className="h-5 w-5 text-primary" />
                  <span className="font-semibold text-gray-700">
                    {t('codeEditor.title')}
                  </span>
                </div>
                <div className="flex space-x-1">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                </div>
              </div>

              {/* Code Editor Mockup */}
              {/* Code Editor Mockup */}
              <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm space-y-2">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500">1</span>
                  <span className="text-purple-400">async function</span>
                  <span className="text-blue-400">resetPassword</span>
                  <span className="text-yellow-400">(email)</span>
                  <span className="text-white">{"{"}</span>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <span className="text-gray-500">2</span>
                  <span className="text-red-400">if</span>
                  <span className="text-white">(</span>
                  <span className="text-blue-300">!isValidEmail(email)</span>
                  <span className="text-white">)</span>
                  <span className="text-white">{"{"}</span>
                </div>
                <div className="flex items-center space-x-2 ml-8">
                  <span className="text-gray-500">3</span>
                  <span className="text-purple-400">throw</span>
                  <span className="text-green-400">'Geçersiz e-posta'</span>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <span className="text-gray-500">4</span>
                  <span className="text-white">{"}"}</span>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <span className="text-gray-500">5</span>
                  <span className="text-purple-400">await</span>
                  <span className="text-blue-300">sendResetLink(email)</span>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <span className="text-gray-500">6</span>
                  <span className="text-purple-400">return</span>
                  <span className="text-green-400">'E-posta gönderildi!'</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500">7</span>
                  <span className="text-white">{"}"}</span>
                </div>
              </div>

              {/* Eğitim Şekilleri */}
              <div className="grid grid-cols-3 gap-3 mt-6">
                <div className="text-center p-3 bg-primary-50 rounded-lg">
                  <Monitor className="h-5 w-5 text-primary-600 mx-auto mb-1" />
                  <div className="text-lg font-bold text-primary-600">
                    {t('education.online')}
                  </div>
                  <div className="text-xs text-primary-600">{t('education.training')}</div>
                </div>
                <div className="text-center p-3 bg-primary-100 rounded-lg">
                  <Users className="h-5 w-5 text-primary-700 mx-auto mb-1" />
                  <div className="text-lg font-bold text-primary-700">
                    {t('education.hybrid')}
                  </div>
                  <div className="text-xs text-primary-700">{t('education.training')}</div>
                </div>
                <div className="text-center p-3 bg-primary-200 rounded-lg">
                  <Building className="h-5 w-5 text-primary-800 mx-auto mb-1" />
                  <div className="text-lg font-bold text-primary-800">
                    {t('education.faceToFace')}
                  </div>
                  <div className="text-xs text-primary-800">{t('education.training')}</div>
                </div>
              </div>

              {/* Programming Languages */}
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  {t('technologies.popular')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {[
                    "JavaScript",
                    "React",
                    "Node.js",
                    "Python",
                    "TypeScript",
                  ].map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 bg-gradient-to-r from-primary to-primary-600 text-white text-xs rounded-full font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -top-3 -right-3 bg-primary-400 rounded-full p-2 shadow-lg animate-bounce">
              <Code className="h-5 w-5 text-primary-900" />
            </div>
            <div className="absolute -bottom-3 -left-3 bg-primary rounded-full p-2 shadow-lg animate-pulse">
              <Terminal className="h-5 w-5 text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
